import React, { useEffect, useState, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SectionBody from "../../components/structure/SectionBody";
import YellowDivider from "../../components/dividers/YellowDivider";
import {
  getAcademyMediaBySlug,
  getLocalisedAcademyMediaByParentId,
} from "../../api/academy";
import { Course } from "../../compilers/course";
import InfoSectionParent from "../../components/article/InfoSectionParent";
import CourseBody from "../../components/course/CourseBody";
import ContentCard from "../../components/contentcards/ContentCard";
import ShareButtons from "../../components/singlecomponents/ShareButtons";
import CourseInfo from "../../components/course/CourseInfo";
import ArticleSkeleton from "../../components/article/ArticleSkeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "../../stylesheet/components/media/MediaMain.scss";
import Button from "../../components/singlecomponents/Button";
import "react-loading-skeleton/dist/skeleton.css";
import CourseSummary from "../../components/course/CourseSummary";
import "../../stylesheet/pages/CourseInner.scss";

const CourseInner: React.FC = () => {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const { slug } = useParams<{ slug: string }>();
  const [course, setCourse] = useState<Course>();
  const [loading, setLoading] = useState<boolean>(true);
  const [courseBegun, setCourseBegun] = useState<boolean>(false);
  const { t, i18n, ready } = useTranslation();
  const history = useHistory();
  const [currentChapterIndex, setCurrentChapterIndex] = useState<number>(1);
  const PreviewCardContainer = useRef<HTMLDivElement>(null);

  //console.log("This course is", course);

  function getLanguage() {
    const url = location.pathname.split("/");
    return url[1];
  }

  useEffect(() => {
    getAcademyMediaBySlug(getLanguage(), slug).then((response: Course) => {
      if (response == undefined) {
        history.replace(`/${i18n.language}/404`);
      } else {
        //console.log("CARD ->", response);
        setCourse(response);

        setLoading(false);
      }
    });
  }, [slug]);

  useEffect(() => {
    if (course) {
      setLoading(true);
      getLocalisedAcademyMediaByParentId(i18n.language, course.data.parent_id)
        .then((response) => {
          //console.log("Article", response);
          history.replace(
            `/${i18n.language}/academy/catalogue/${response.data.slug}`
          );
          setCourse(response);
          setLoading(false);
        })
        .catch((error) => {
          history.push(`/${i18n.language}/404`);
          console.log(error);
        });
    }
  }, [i18n.language]);

  if (loading) {
    return <ArticleSkeleton />;
  }

  const nextChapter = () => {
    setCurrentChapterIndex(currentChapterIndex + 1);
  };

  const previousChapter = () => {
    setCurrentChapterIndex(currentChapterIndex - 1);
  };

  const handleSetChapterByIndex = (index: number) => {
    setCurrentChapterIndex(index);
  };

  const handleReturnToSummary = () => {
    scrollToTop();
    setCourseBegun(false);
    setCurrentChapterIndex(1);
  };

  const handleBeginCourse = () => {
    scrollToTop();
    setCourseBegun(true);
  };

  const handleBeginCourseAtChapter = (selectedChapter: number) => {
    scrollToTop();
    setCurrentChapterIndex(selectedChapter);
    setCourseBegun(true);
  };

  const getBody = () => {
    if (courseBegun) {
      return (
        <CourseBody
          returnToSummary={handleReturnToSummary}
          course={course}
          currentChapterIndex={currentChapterIndex}
          nextChapter={nextChapter}
          previousChapter={previousChapter}
          setCurrentChapter={handleSetChapterByIndex}
        />
      );
    } else {
      return (
        <CourseSummary
          course={course}
          handleBeginCourseAtChapter={handleBeginCourseAtChapter}
        />
      );
    }
  };

  const getInfo = () => {
    if (courseBegun) {
      if (PreviewCardContainer.current)
        PreviewCardContainer.current?.style.setProperty("display", "none");
      return (
        <div className="hidden-mobile">
          <div className="media-inner-share my-2">
            <InfoSectionParent label={t("inner-page.share")} />
            <ShareButtons />
          </div>
          <CourseInfo
            course={course}
            setCurrentChapter={handleSetChapterByIndex}
            currentChapterIndex={currentChapterIndex}
          />
          <div className="back-btn" onClick={handleReturnToSummary}>
            <Button
              bootstrapClass="px-sm-2 px-md-4 py-1 mt-3"
              name={t("button.back-course")}
            />
          </div>
        </div>
      );
    } else {
      if (PreviewCardContainer.current)
        PreviewCardContainer.current?.style.setProperty("display", "initial");
      return (
        <button
          className={`p2 btn btn-second next-btn`}
          onClick={handleBeginCourse}
        >
          {ready ? t("inner-page.start-reading") : ""}
        </button>
      );
    }
  };

  //Sets the Blue pill and Yellow pill texts on the content card
  const getContentCard = () => {
    if (course) {
      course.data.bluePillText = course.data.difficulty ?? "";
      course.data.yellowPillText =
        course.data.learningPathways.length > 0
          ? course.data.learningPathways[0]
          : undefined;
      course.data.category = course.data.type;
      return (
        <ContentCard
          ClassName="card-preview d-block"
          media={course}
          section_type="academy"
        />
      );
    }
  };

  return (
    <div className="pt-4">
      <SectionBody>
        <div className="media-inner-section course-summary-page">
          {/* COURSE SUMMARY - LEFT PANEL */}
          <div className="course-summary">
            <div className="course-summary__info-container">
              <div className="mb-3 hidden-mobile">
                <h1 className="h2">{course.data.title}</h1>
                <YellowDivider heading="h1" />
              </div>
              {getBody()}
            </div>
          </div>
          {/* COURSE SUMMARY - RIGHT PANEL */}
          <div className="summary-wrapper">
            {course !== undefined && (
              <>
                <div className="card-preview-container">
                  <div className="media-inner-info">
                    <div ref={PreviewCardContainer} className="card-container">
                      {getContentCard()}
                    </div>
                    {getInfo()}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </SectionBody>
    </div>
  );
};

export default CourseInner;
